import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiClientError, getFriendlyErrorMessage } from "@/lib/api-client";
import { Notify } from "@/components/ui/notify";
import { isDraftEntityId, isDraftLineId } from "@/features/lines/canvas/draft-canvas-adapter";
import type {
  CreateMachineRequest,
  UpdateMachineRequest,
  DuplicateMachineRequest,
  CreateParameterRequest,
  UpdateLineRequest,
  ConnectionsRequest,
  UpsertCompatibilityRequest,
} from "@opus-dt/shared-types";
import type {
  MachineDetail,
  LineDetail,
  ParameterDetail,
  CompatibilityRow,
} from "../../types/line-editor.types.js";

export type {
  MachineDetail,
  ConnectionDetail,
  LineDetail,
  ParameterDetail,
  CompatibilityRow,
} from "../../types/line-editor.types.js";

const lineKey = (id: string) => ["lines", id] as const;
const parametersKey = (machineId: string) => ["parameters", machineId] as const;
const compatibilityKey = (lineId: string) => ["compatibility", lineId] as const;

export function useLineDetail(id: string) {
  return useQuery<LineDetail>({
    queryKey: lineKey(id),
    queryFn: () => api.get<LineDetail>(`/api/v1/lines/${id}`),
    enabled: !!id && !isDraftLineId(id),
  });
}

export function useUpdateLine(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateLineRequest & { confirmRealLine?: boolean }) =>
      api.patch(`/api/v1/lines/${lineId}`, data, {
        headers: data.confirmRealLine ? { "X-Confirm-Real-Line": "true" } : undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lines"] });
      void qc.invalidateQueries({ queryKey: lineKey(lineId) });
      // Spec 081 — trocar a unidade de produção não reescreve nenhum lançamento, mas muda o RÓTULO
      // de todo número exibido. Uma tela que não invalida passa a mostrar "kg" escrito sobre número
      // em pacote, que é pior do que não mostrar nada.
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
      void qc.invalidateQueries({ queryKey: ["oee", lineId] });
    },
    meta: { skipErrorToast: true }, // call sites já tratam erro localmente
  });
}

export function useCreateMachine(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMachineRequest) =>
      api.post<MachineDetail>(`/api/v1/lines/${lineId}/machines`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
    },
    meta: { skipErrorToast: true },
  });
}

// Duplica a configuração de uma máquina (parâmetros + compatibilidade).
// A cópia não entra no canvas; invalida a lista para o clone aparecer.
export function useDuplicateMachine(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ machineId, name }: { machineId: string } & DuplicateMachineRequest) =>
      api.post<MachineDetail>(
        `/api/v1/lines/${lineId}/machines/${machineId}/duplicate`,
        name ? { name } : {}
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lineKey(lineId) });
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
    },
    meta: { skipErrorToast: true },
  });
}

export function useDeleteMachine(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (machineId: string) => api.delete(`/api/v1/lines/${lineId}/machines/${machineId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lineKey(lineId) });
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
      void qc.invalidateQueries({ queryKey: ["canvas", lineId] });
    },
    meta: { skipErrorToast: true },
  });
}

export function useUpdateMachine(lineId: string, machineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateMachineRequest) =>
      api.patch<MachineDetail>(`/api/v1/lines/${lineId}/machines/${machineId}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lineKey(lineId) });
      void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
      void qc.invalidateQueries({ queryKey: ["canvas", lineId] });
    },
    meta: { skipErrorToast: true },
  });
}

// Spec 081 — criar, alterar ou remover um CONTADOR muda o que a linha mede: `useLineMachines`
// alimenta `useLineTrackedByMachine` (esconde o apontamento no nível da linha) e o badge de
// contador no canvas. Sem esta invalidação a tela segue oferecendo um apontamento que a rota já
// recusa, e o inverso — escondendo o único caminho que sobrou.
function invalidateLineMachines(qc: ReturnType<typeof useQueryClient>, lineId?: string) {
  if (!lineId) return;
  void qc.invalidateQueries({ queryKey: ["lines", lineId, "machines"] });
}

export function useUpdateParameter(machineId: string, lineId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateParameterRequest> }) =>
      api.patch<ParameterDetail>(`/api/v1/parameters/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: parametersKey(machineId) });
      // Spec 080 — tirar a marca de RESTRIÇÃO solta a máquina que ela segurava parada
      // (`restriction-release.ts`): status, evento de parada e downtime mudam no mesmo ato.
      void qc.invalidateQueries({ queryKey: ["machine", machineId] });
      invalidateLineMachines(qc, lineId);
    },
    meta: { skipErrorToast: true },
  });
}

export function usePutConnections(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ConnectionsRequest) =>
      api.put<{ count: number }>(`/api/v1/lines/${lineId}/connections`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: lineKey(lineId) }),
  });
}

export function useParameters(machineId: string) {
  return useQuery<ParameterDetail[]>({
    queryKey: parametersKey(machineId),
    queryFn: () => api.get<ParameterDetail[]>(`/api/v1/machines/${machineId}/parameters`),
    enabled: !!machineId && !isDraftEntityId(machineId),
  });
}

export function useCreateParameter(machineId: string, lineId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateParameterRequest) =>
      api.post<ParameterDetail>(`/api/v1/machines/${machineId}/parameters`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: parametersKey(machineId) });
      invalidateLineMachines(qc, lineId);
    },
    meta: { skipErrorToast: true },
  });
}

export function useDeleteParameter(machineId: string, lineId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/parameters/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: parametersKey(machineId) });
      // Spec 080 — o `["machine", mId]` já estava aqui e ganhou peso novo: excluir um parâmetro de
      // restrição solta a máquina que ele segurava parada.
      void qc.invalidateQueries({ queryKey: ["machine", machineId] });
      void qc.invalidateQueries({ queryKey: ["sensors", machineId] });
      invalidateLineMachines(qc, lineId);
      Notify.toast({ variant: "success", title: "Parâmetro excluído" });
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.error.code === "PARAMETER_HAS_ACTIVE_LINKS") {
        Notify.toast({
          variant: "warning",
          title: "Não foi possível excluir",
          description: err.error.message,
        });
        return;
      }
      Notify.toast({
        variant: "danger",
        title: "Falha ao excluir parâmetro",
        description: getFriendlyErrorMessage(err, "Tente novamente."),
      });
    },
    meta: { skipErrorToast: true },
  });
}

export function useCompatibility(lineId: string) {
  return useQuery<CompatibilityRow[]>({
    queryKey: compatibilityKey(lineId),
    queryFn: () => api.get<CompatibilityRow[]>(`/api/v1/lines/${lineId}/compatibility`),
    enabled: !!lineId && !isDraftLineId(lineId),
  });
}

export function useUpsertCompatibility(lineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpsertCompatibilityRequest) =>
      api.put(`/api/v1/lines/${lineId}/compatibility`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: compatibilityKey(lineId) });
      // Com resolução AO VIVO, editar o perfil do par MUST refletir no próximo
      // preview do sandbox sem F5; a chave do preview é por cenário+período e não enxerga a
      // matriz, então a invalidação é explícita (nunca confiar no staleTime — guardrail #12).
      void qc.invalidateQueries({ queryKey: ["scenarios"] });
    },
    // Sem `skipErrorToast`: a aba de compatibilidades deixou de traduzir o erro na mão
    // (o toast amigável do MutationCache global cobre os dois call sites).
    meta: { errorTitle: "Não foi possível salvar a compatibilidade" },
  });
}
